import * as React from 'react'
import {ChangeEvent, Component, ReactElement, RefObject} from 'react'
import {Box} from '@mui/material'
import {NatApplicationRepo} from '../../repository/NatApplicationRepo'
import {BlockSpacer, currentMedia, GridContainer, GridItem} from '../../../../../styledComponents/ResponsiveConvenience'
import {FlexContainer, FullWidthColumnFlexContainer} from '../../../../../styledComponents/FlexComponents'
import {convertToRoomNumberForResident} from '../../../../common/util/Natutil'
import NatCurrentDateTimeRepo from '../../../currentDateTime/repository/NatCurrentDateTimeRepo'
import moment from 'moment'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {
    ApplyButton,
    ItemHeading,
    ListGridContainer,
    WideWhiteArea,
} from '../../common/resident/NatResidentApplicationsCommon'
import {NatGetCurrentDateTime} from '../../../currentDateTime/repository/NatGetCurrentDateTime'
import {
    NatResidentWindowFilmAttachInfo,
    NatResidentWindowFilmAttachLocationState,
} from '../NatResidentApplicationInterface'
import {Nullable} from '../../../../../app/TypeHelpers'
import EventEmitterExResident from '../../../../../components/shared/pageBaseComponents/EventEmitterExResident'
import NatCondominiumRepo from '../../../condominium/repository/NatCondominiumRepo'
import NatResidentApplicationDetailFormItem from '../../common/resident/NatResidentApplicationDetailFormItem'
import HolidaysRepository from '../../../../../components/HolidaysRepository'
import {AsyncCallFunctionType} from '../../../../../components/shared/hoc/withAsyncCommunication'
import {PPTextFieldForResident} from '../../../../../components/shared/forms/resident/PPTextFieldForResident'
import PPDatePickerForResident from '../../../../../components/shared/forms/resident/PPDatePickerForResident'
import {HKConstants} from '../../../../../HKConstants'
import ErrorMessageAreaWrapper from '../../../../../components/shared/forms/resident/ErrorMessageAreaWrapper'
import ResidentAttachmentFileArea, {
    FileInformation,
} from '../../../../../components/applications/ResidentAttachmentFileArea'
import {DefaultStyledButton, StyledSecondaryButton} from '../../../../../styledComponents/Button'
import {NatZipCode} from '../../../condominium/repository/NatZipCode'
import {RequestItemError} from '../../../../../network/response/RequestItemError'
import {Messages} from '../../../../../Messages'
import {
    DisplayItemInputTitleNoMargin,
    DisplayItemInputTitleRequiredNoMargin,
} from '../../../../../styledComponents/DisplayItem'
import styled from 'styled-components'
import {Colors} from '../../../../../styledComponents/Constants'
import PPRadioGroupForResident from '../../../../../components/shared/forms/resident/PPRadioGroupForResident'
import {CONTRACTOR_REQUEST} from '../../../../../CodeMaster'
import {Location, NavigateFunction} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    condominiumRepo: NatCondominiumRepo
    holidaysRepo: HolidaysRepository
    navigate: NavigateFunction
    location: Location<NatResidentWindowFilmAttachLocationState>
    eventEmitter: EventEmitterExResident
    asyncCallFunction: AsyncCallFunctionType

    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    windowFilmAttachDate: Nullable<string>
    contractorRequest: Nullable<string>
    contractorName: Nullable<string>
    contractorZipCode: Nullable<string>
    contractorPrefecture: Nullable<string>
    contractorAddress2: Nullable<string>
    contractorAddress3: Nullable<string>
    contractorBuildingRoomNumber: Nullable<string>
    contractorPhoneNumber: Nullable<string>
    contractorRep: Nullable<string>
    applyToAttachment: Nullable<FileInformation[]>

    submitButtonLabel: string
    onClickApplyConfirmButton: (
        applyRequest: NatResidentWindowFilmAttachInfo,
    ) => Promise<void>
}

interface State {
    applicantFamilyName: string
    applicantFirstName: string
    windowFilmAttachDate: string
    contractorRequest: string
    contractorName: string
    contractorZipCode: string
    contractorPrefecture: string
    contractorAddress2: string
    contractorAddress3: string
    contractorBuildingRoomNumber: string
    contractorPhoneNumber: string
    contractorRep: string
    applyToAttachment: FileInformation[]

    currentDate: moment.Moment
    holidays: string[]

    applyButtonDisabled: boolean
    applyFileAddDisable: boolean
    applyToAttachmentAddDisplay: boolean
    applyToAttachmentNoSelect: boolean
    isLoadCompleted: boolean
}

export default class NatResidentWindowFilmAttachForm extends Component<Props, State> {
    attachmentRef: RefObject<HTMLInputElement>

    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            applicantFamilyName: '',
            applicantFirstName: '',
            windowFilmAttachDate: '',
            contractorRequest: CONTRACTOR_REQUEST.REQUEST.value,
            contractorName: '',
            contractorZipCode: '',
            contractorPrefecture: '',
            contractorAddress2: '',
            contractorAddress3: '',
            contractorBuildingRoomNumber: '',
            contractorPhoneNumber: '',
            contractorRep: '',
            applyToAttachment: [],
            currentDate: moment(),
            holidays: [],
            applyButtonDisabled: true,
            applyFileAddDisable: false,
            applyToAttachmentAddDisplay: true,
            applyToAttachmentNoSelect: false,
            isLoadCompleted: false,
        }

        this.attachmentRef = React.createRef()

        this.handleChangeText = this.handleChangeText.bind(this)
        this.handleChangeWindowFilmAttachDate = this.handleChangeWindowFilmAttachDate.bind(this)
        this.handleChangeContractorRequest = this.handleChangeContractorRequest.bind(this)
        this.handleApplyToAttachmentInput = this.handleApplyToAttachmentInput.bind(this)
        this.handleApplyToAttachmentDelete = this.handleApplyToAttachmentDelete.bind(this)
        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        try {
            const [currentTimeResponse, holidays] = await Promise.all([
                this.getCurrentDateTime(),
                this.getHoliday(),
            ])

            if (currentTimeResponse && currentTimeResponse.details) {
                this.setState({
                    currentDate: moment(currentTimeResponse.details.currentDateTime),
                })
            }

            const applicantFamilyName = this.props.applicantFamilyName ?? this.state.applicantFamilyName
            const applicantFirstName = this.props.applicantFirstName ?? this.state.applicantFirstName
            const windowFilmAttachDate = this.props.windowFilmAttachDate ?? this.state.windowFilmAttachDate
            const contractorRequest = this.props.contractorRequest ?? this.state.contractorRequest
            const contractorName = this.props.contractorName ?? this.state.contractorName
            const contractorZipCode = this.props.contractorZipCode ?? this.state.contractorZipCode
            const contractorPrefecture = this.props.contractorPrefecture ?? this.state.contractorPrefecture
            const contractorAddress2 = this.props.contractorAddress2 ?? this.state.contractorAddress2
            const contractorAddress3 = this.props.contractorAddress3 ?? this.state.contractorAddress3
            const contractorBuildingRoomNumber =
                this.props.contractorBuildingRoomNumber ?? this.state.contractorBuildingRoomNumber
            const contractorPhoneNumber = this.props.contractorPhoneNumber ?? this.state.contractorPhoneNumber
            const contractorRep = this.props.contractorRep ?? this.state.contractorRep
            const applyToAttachment = this.props.applyToAttachment ?? this.state.applyToAttachment

            this.setState({
                applicantFamilyName: applicantFamilyName,
                applicantFirstName: applicantFirstName,
                windowFilmAttachDate: windowFilmAttachDate,
                contractorRequest: contractorRequest,
                contractorName: contractorName ? contractorName : '',
                contractorZipCode: contractorZipCode ? contractorZipCode : '',
                contractorPrefecture: contractorPrefecture ? contractorPrefecture : '',
                contractorAddress2: contractorAddress2 ? contractorAddress2 : '',
                contractorAddress3: contractorAddress3 ? contractorAddress3 : '',
                contractorBuildingRoomNumber: contractorBuildingRoomNumber ? contractorBuildingRoomNumber : '',
                contractorPhoneNumber: contractorPhoneNumber ? contractorPhoneNumber : '',
                contractorRep: contractorRep ? contractorRep : '',
                applyToAttachment: applyToAttachment,
                holidays: holidays === null ? [] : holidays,
            }, () => {
                this.updateApplyConfirmButtonDisabled()
            })
        } finally {
            this.setState({isLoadCompleted: true})
        }
        this.props.eventEmitter.closeLoading()
    }

    async getCurrentDateTime(): Promise<Nullable<NatGetCurrentDateTime>> {
        const {currentDateTimeRepo, eventEmitter} = this.props

        return await this.props.asyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    async getHoliday(): Promise<Nullable<string[]>> {
        const {holidaysRepo, eventEmitter} = this.props

        const minDate: string = HKConstants.CMN.SYC.MIN_HOLIDAY_DATE
        const maxDate: string = HKConstants.CMN.SYC.MAX_HOLIDAY_DATE
        return await this.props.asyncCallFunction(
            holidaysRepo.getHolidays.bind(holidaysRepo, minDate, maxDate),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    handleChangeText(event: ChangeEvent<HTMLInputElement>): void {
        const key = event.target.name as keyof State
        const value: string = event.target.value
        this.setState({...this.state, [key]: value ? value : null}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangeWindowFilmAttachDate(date: string): void {
        this.setState({windowFilmAttachDate: date}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangeContractorRequest(event: ChangeEvent<HTMLInputElement>): void {
        this.setState({contractorRequest: event.target.value}, () => {
            this.updateInitChangeContractorRequest()
            this.updateApplyConfirmButtonDisabled()
        })
    }

    updateInitChangeContractorRequest(): void {
        if (this.state.contractorRequest === CONTRACTOR_REQUEST.NOT_REQUEST.value) {
            this.setState({
                contractorName: '',
                contractorZipCode: '',
                contractorPrefecture: '',
                contractorAddress2: '',
                contractorAddress3: '',
                contractorBuildingRoomNumber: '',
                contractorPhoneNumber: '',
                contractorRep: '',
            })
        }
    }

    handleClickAddFileButton(elementId: string): void {
        const fileUpload = document.getElementById(elementId) as HTMLInputElement
        fileUpload.value = ''
        if (fileUpload) {
            fileUpload.click()
        }
    }

    handleApplyToAttachmentInput(event: React.ChangeEvent<HTMLInputElement>): void {
        if (event.target.files && event.target.files[0] && event.target.files[0].name) {
            const {applyToAttachment} = this.state
            const fileName = event.target.files[0].name
            const reader: FileReader = new FileReader()
            reader.onload = () => {
                const fileData: string[] = String(reader.result).split(',')
                if (fileData[1]) {
                    applyToAttachment.push({
                        fileName: fileName,
                        fileData: fileData[1],
                        fileIndex: applyToAttachment.length,
                        fileNumber: null,
                        deleteFlag: false,
                    })
                    this.setState({
                        applyToAttachment: applyToAttachment,
                    }, () => {
                        this.updateApplyConfirmButtonDisabled()
                    })
                }
            }
            reader.readAsDataURL(event.target.files[0])
        }
        if (this.attachmentRef.current && this.attachmentRef.current.value) {
            this.attachmentRef.current.value = ''
        }
    }

    handleApplyToAttachmentDelete(file: FileInformation): void {
        const fileIndex = file.fileIndex ?? 0
        const applyToAttachmentList = this.state.applyToAttachment
        applyToAttachmentList[fileIndex] = file

        this.setState({
            applyToAttachment: applyToAttachmentList,
        }, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    updateApplyConfirmButtonDisabled(): void {
        const {
            applicantFamilyName, applicantFirstName, windowFilmAttachDate, contractorRequest,
            contractorName, contractorPrefecture, contractorAddress2, contractorAddress3,
            contractorPhoneNumber, contractorRep, applyToAttachment,
        } = this.state

        let applyButtonDisabled = false
        const addFileCount = applyToAttachment.filter(file => !file.deleteFlag)

        if (!applicantFamilyName || !applicantFirstName || !windowFilmAttachDate ||
            !contractorRequest || !(addFileCount.length >= 1)) {
            applyButtonDisabled = true
        }

        if (contractorRequest === CONTRACTOR_REQUEST.REQUEST.value) {
            if (!applicantFamilyName || !applicantFirstName || !windowFilmAttachDate ||
                !contractorRequest || !contractorName || !contractorPrefecture || !contractorAddress2 ||
                !contractorAddress3 || !contractorPhoneNumber || !contractorRep || !(addFileCount.length >= 1)) {
                applyButtonDisabled = true
            } else {
                applyButtonDisabled = false
            }
        }
        this.setState({
            applyButtonDisabled: applyButtonDisabled,
            applyToAttachmentAddDisplay: !(addFileCount.length >= 20),
            applyToAttachmentNoSelect: (addFileCount.length === 0),
        })
    }

    async onClickApplyConfirmButton(): Promise<void> {
        const {
            applicantFamilyName, applicantFirstName, windowFilmAttachDate, contractorRequest,
            contractorName, contractorZipCode, contractorPrefecture,
            contractorAddress2, contractorAddress3, contractorBuildingRoomNumber,
            contractorPhoneNumber, contractorRep, applyToAttachment,
        } = this.state

        const applyContent: NatResidentWindowFilmAttachInfo = {
            applicantFamilyName: applicantFamilyName,
            applicantFirstName: applicantFirstName,
            windowFilmAttachDate: windowFilmAttachDate,
            contractorRequest: contractorRequest,
            contractorName: contractorName || null,
            contractorZipCode: contractorZipCode || null,
            contractorPrefecture: contractorPrefecture || null,
            contractorAddress2: contractorAddress2 || null,
            contractorAddress3: contractorAddress3 || null,
            contractorBuildingRoomNumber: contractorBuildingRoomNumber || null,
            contractorPhoneNumber: contractorPhoneNumber || null,
            contractorRep: contractorRep || null,
            applyToAttachment: applyToAttachment.filter(attachment => !attachment.deleteFlag),
        }

        if (!applyContent) {
            return
        }
        await this.props.onClickApplyConfirmButton(applyContent)
    }

    async getAddressFromZipCode(): Promise<void> {
        const {contractorZipCode} = this.state

        const {condominiumRepo} = this.props

        await this.props.eventEmitter.showLoading()

        try {
            // 桁数不正の場合、単純に結果が取得できないので特に事前チェックは行わない
            let firstPart = ''
            let lastPart = ''
            if (contractorZipCode) {
                firstPart = contractorZipCode.substring(0, 3)
                lastPart = contractorZipCode.substring(3)
            }
            await condominiumRepo.getZipCode(firstPart, lastPart).then(
                zipData => {
                    const zipCodeResponse: NatZipCode = zipData
                    if (zipCodeResponse && zipCodeResponse.data
                        && zipCodeResponse.data[0] && zipCodeResponse.data[0].ja) {
                        this.setState({
                            contractorPrefecture: zipCodeResponse.data[0].ja.prefecture,
                            contractorAddress2: zipCodeResponse.data[0].ja.address1,
                            contractorAddress3: zipCodeResponse.data[0].ja.address2,
                        }, () => {
                            this.updateApplyConfirmButtonDisabled()
                        })
                    }
                    if (zipCodeResponse && zipCodeResponse.data
                        && zipCodeResponse.data.length > 1) {
                        const requestItemError = new RequestItemError(
                            'contractorZipCode', Messages.WCONA0002.toString(), 'WCONA0002')
                        this.props.eventEmitter.emit('contractorZipCode', requestItemError)

                    }
                },
            ).catch(
                () => {
                    const requestItemError = new RequestItemError(
                        'contractorZipCode', Messages.ECONA0001.toString(), 'ECONA0001')
                    this.props.eventEmitter.emit('contractorZipCode', requestItemError)
                },
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    renderApplicantForm(): ReactElement {
        const {applicantFamilyName, applicantFirstName} = this.state

        const applicantRoom = NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_NAME) + '　' +
            NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_RIDGENAME) + ' ' +
            convertToRoomNumberForResident(
                NatResidentLoginInfoSessionStorageHelper
                    .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_ROOM_NUMBER),
            )

        return (
            <>
                <ItemHeading>
                    申請者
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    {applicantRoom}
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'氏名'}
                        required
                        component={
                            <FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFamilyName',
                                        maxLength: 10,
                                        value: applicantFamilyName,
                                        placeholder: '例：素敵',
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFamilyName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                                <FlexContainer>
                                    <BlockSpacer width={5}/>
                                </FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFirstName',
                                        maxLength: 10,
                                        placeholder: '例：太郎',
                                        value: applicantFirstName,
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFirstName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </FlexContainer>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderWindowFilmAttachDateForm(): ReactElement {
        const {windowFilmAttachDate, currentDate, holidays} = this.state

        return (
            <>
                <ItemHeading>
                    窓ガラスフィルムの貼付日
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'貼付日'}
                        required
                        component={
                            <PPDatePickerForResident
                                date={windowFilmAttachDate}
                                onChange={this.handleChangeWindowFilmAttachDate}
                                holidays={holidays}
                                itemValue={''}
                                eventKeys={['windowFilmAttachDate']}
                                eventEmitter={this.props.eventEmitter}
                                today={currentDate.toDate()}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderContractorRequestForm(): ReactElement {
        const {contractorRequest} = this.state
        return (
            <>
                <ItemHeading>
                    施工依頼
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <DisplayItemInputTitleRequiredNoMargin>
                        業者に依頼する・しないを選択
                    </DisplayItemInputTitleRequiredNoMargin>
                    <Box height={'20px'}/>
                    <GridContainer>
                        <GridItem over={12}>
                            <PPRadioGroupForResident
                                data={CONTRACTOR_REQUEST.values()}
                                selectValue={contractorRequest}
                                onChange={this.handleChangeContractorRequest}
                                eventKeys={['contractorRequest']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        </GridItem>
                    </GridContainer>
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderContractorForm(): ReactElement {
        const {
            contractorName,
            contractorZipCode,
            contractorPrefecture,
            contractorAddress2,
            contractorAddress3,
            contractorBuildingRoomNumber,
            contractorPhoneNumber,
            contractorRep,
        } = this.state
        return (
            <>
                <ItemHeading>
                    窓ガラスフィルム貼付施工者
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'業者名'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'contractorName',
                                    value: contractorName,
                                    placeholder: '例：株式会社長谷工リフォーム',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['contractorName']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'郵便番号（ハイフン不要）'}
                        component={
                            <ErrorMessageAreaWrapper>
                                <FlexContainer>
                                    <Box flex={2} minWidth={0}>
                                        <PPTextFieldForResident
                                            inputProps={{
                                                maxLength: 7,
                                                name: 'contractorZipCode',
                                                value: contractorZipCode,
                                                placeholder: '例：1234567',
                                            }}
                                            onChange={this.handleChangeText}
                                            eventKeys={['contractorZipCode']}
                                            eventEmitter={this.props.eventEmitter}
                                        />
                                    </Box>
                                    <FlexContainer>
                                        <BlockSpacer width={5}/>
                                    </FlexContainer>
                                    <Box flex={1} maxWidth={'160px'} minWidth={'125px'} maxHeight={'50px'}>
                                        <DefaultStyledButton
                                            onClick={() => this.getAddressFromZipCode()}>
                                            住所自動入力
                                        </DefaultStyledButton>
                                    </Box>
                                </FlexContainer>
                            </ErrorMessageAreaWrapper>
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'都道府県'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 4,
                                    name: 'contractorPrefecture',
                                    value: contractorPrefecture,
                                    placeholder: '例：東京都',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['contractorPrefecture']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'市区町村'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'contractorAddress2',
                                    value: contractorAddress2,
                                    placeholder: '例：港区',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['contractorAddress2']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'番地'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'contractorAddress3',
                                    value: contractorAddress3,
                                    placeholder: '例：芝４－６－６',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['contractorAddress3']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'建物名・部屋番号'}
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'contractorBuildingRoomNumber',
                                    value: contractorBuildingRoomNumber,
                                    placeholder: '例：長谷工芝二ビル　５F',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['contractorBuildingRoomNumber']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={<span>電話番号<BreakPcOnly/>（ハイフン不要）</span>}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 11,
                                    name: 'contractorPhoneNumber',
                                    value: contractorPhoneNumber,
                                    placeholder: '例：0312345678',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['contractorPhoneNumber']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'担当者'}
                        required
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 20,
                                    name: 'contractorRep',
                                    value: contractorRep,
                                    placeholder: '例：素敵　太郎',
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['contractorRep']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderApplyToAttachmentListInputForm(): ReactElement {
        const {applyToAttachment, applyToAttachmentAddDisplay, applyToAttachmentNoSelect} = this.state
        return (
            <>
                <ItemHeading>
                    添付資料
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    本申請に関連する下記資料はこちらから提出してください。
                    <Box height={'10px'}/>
                    <ListGridContainer fontSize={'16px'}>
                        <Box mb={'10px'}>・</Box>
                        <DisplayItemInputTitleRequiredNoMargin>
                            <Box component={'span'} color={Colors.Body}>仕様書</Box>
                        </DisplayItemInputTitleRequiredNoMargin>
                        <Box mb={'10px'}>・</Box>
                        <DisplayItemInputTitleRequiredNoMargin>
                            <Box component={'span'} color={Colors.Body}>取付け位置図</Box>
                        </DisplayItemInputTitleRequiredNoMargin>
                        <Box mb={'10px'}>・</Box>
                        <DisplayItemInputTitleRequiredNoMargin>
                            <Box component={'span'} color={Colors.Body}>ガラスの熱割れ計算書</Box>
                        </DisplayItemInputTitleRequiredNoMargin>
                        <Box mb={'10px'}>・</Box>
                        <div>
                            <Box component={'span'} color={Colors.Body}>その他資料</Box>
                        </div>
                    </ListGridContainer>
                    <Box height={'20px'}/>
                    <DisplayItemInputTitleNoMargin>
                        添付資料を選択
                    </DisplayItemInputTitleNoMargin>
                    <Box height={'5px'}/>
                    <FileRemarkContainer fontSize={'12px'}>
                        <div>・</div>
                        <div>
                            20ファイルまで添付可能です。
                        </div>
                        <div>・</div>
                        <div>
                            ファイルは画像またはPDFでご提出ください。
                        </div>
                    </FileRemarkContainer>
                    <Box height={'10px'}/>
                    {applyToAttachmentAddDisplay &&
                        <StyledSecondaryButton
                            onClick={() => this.handleClickAddFileButton('fileUpload-attachmentFile')}>
                            {applyToAttachmentNoSelect ?
                                'ファイルを選択する' : 'ファイルを追加する'
                            }
                        </StyledSecondaryButton>
                    }
                    <DisplayNoneFileInput
                        type={'file'}
                        multiple
                        id={'fileUpload-attachmentFile'}
                        ref={this.attachmentRef}
                        onChange={this.handleApplyToAttachmentInput}
                    />
                    <Box height={'30px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'選択中の添付資料'}
                        component={
                            !applyToAttachmentNoSelect ?
                                applyToAttachment.map((attachmentFile, index) => (
                                    !applyToAttachment[index].deleteFlag &&
                                    <ResidentAttachmentFileArea
                                        key={index}
                                        fileInformation={applyToAttachment[index]}
                                        index={index}
                                        onDelete={this.handleApplyToAttachmentDelete}
                                        eventKeys={[`file[${index}]`]}
                                        eventEmitter={this.props.eventEmitter}
                                    />
                                )) : <StyledNoFile>選択されていません</StyledNoFile>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>)
    }

    render(): ReactElement {
        const {submitButtonLabel} = this.props
        const {applyButtonDisabled, contractorRequest} = this.state

        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                {this.renderApplicantForm()}
                < Box height={'15px'}/>
                {this.renderWindowFilmAttachDateForm()}
                < Box height={'15px'}/>
                {this.renderContractorRequestForm()}
                <Box height={'15px'}/>
                {(contractorRequest === CONTRACTOR_REQUEST.REQUEST.value) ?
                    <>
                        {this.renderContractorForm()}
                        <Box height={'15px'}/>
                    </> : null
                }
                {this.renderApplyToAttachmentListInputForm()}
                <ApplyButton disabled={applyButtonDisabled} onClick={this.onClickApplyConfirmButton}>
                    <FullWidthColumnFlexContainer>
                        <div>
                            {submitButtonLabel}
                        </div>
                    </FullWidthColumnFlexContainer>
                </ApplyButton>
            </>
        )
    }
}

const DisplayNoneFileInput = styled.input`
  display: none;
`
const StyledNoFile = styled.div`
  color: ${Colors.AccentLight};
  ${currentMedia.lessThan('sp')`
    margin-left: 18px;
  `};
`
const FileRemarkContainer = styled(ListGridContainer)`
  color: ${Colors.AccentDark};
`
const BreakPcOnly = styled('br')`
  ${currentMedia.lessThan('sp')`
    display: none;
  `};
`
